<template>
  <base-modal
    no-close-on-backdrop
    centered
    size="xl"
    title="Envie uma planilha com seus produtos"
    :open.sync="open"
    :loading="loading"
  >
    <b-row>
      <b-col cols="12">
        <b-alert show variant="warning">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          A planilha deverá ter obrigatoriamente ao menos o
          <b>código de barra do produto e preço.</b>
        </b-alert>
      </b-col>
      <b-col cols="12" class="mt-3 mb-4" v-if="!successfully && !loading">
        <b-form-file
          multiple
          ref="uploadFile"
          size="lg"
          accept=".xlsx, .xls, .csv"
          placeholder="Selecione ou arraste sua planilha aqui (Excel ou csv)"
          drop-placeholder="Arraste a planilha aqui"
          :file-name-formatter="formatNames"
          v-model="fields.files"
        ></b-form-file>
        <error-message :errorMessage="errorMessages.files" />
        <ul class="ml-0 pl-4 mt-2">
          <li v-for="(file, index) in fields.files" :key="index">
            <span class="fs-14">
              Arquivo <b>{{ file.name }}</b> selecionado
            </span>
            <b-badge
              title="Remover arquivo"
              variant="danger"
              class="ml-2 pointer"
              @click="deleteFile(index)"
            >
              <i class="far fa-trash-alt"></i>
            </b-badge>
            <span v-if="file.invalid" class="text-danger">
              (Arquivo inválido, verifique-o e tente novamente)
            </span>
          </li>
        </ul>
      </b-col>
      <b-col
        cols="12"
        class="text-center my-4"
        v-else-if="!successfully && loading"
      >
        <b-spinner label="Spinning"></b-spinner>
      </b-col>
      <b-col v-else>
        <b-jumbotron>
          <template #header> Arquivos enviados com sucesso!</template>

          <template #lead>
            Agora é só esperar. Em até 2 dias úteis nossa equipe irá
            disponibilizar seus produtos.
          </template>

          <hr class="my-4" />

          <p class="mb-2">
            Esqueceu de algum produto ou de alguma informação como preço ou
            código de barras? É só enviar outro(s) arquivo(s) novamente para
            nossa equipe.
          </p>

          <b-btn variant="success" @click="toggleOpen()">
            <i class="fas fa-check mr-2"></i>
            OK
          </b-btn>
        </b-jumbotron>
      </b-col>
      <b-col cols="12">
        <b-alert show variant="info">
          <p>
            <i class="fas fa-info-circle mr-1"></i>
            Os produtos encontrados no banco de dados serão disponibilizados com
            preço no "produtos selecionados" em até
            <b>2 (dois) dias úteis</b>.
          </p>
          <p class="ml-4">
            Os produtos não localizados serão enviados para a equipe Constr Up
            cadastrá-los.
          </p>
          <p class="ml-4">
            A não informação de estoque será considerado como
            <b>"estoque infinito"</b>, ou seja, será disponibilizado no app para
            compra sem limite de quantidade.
          </p>
        </b-alert>
      </b-col>
    </b-row>

    <template #modal-footer>
      <b-btn variant="danger" @click="toggleOpen()" :disabled="loading">
        <i class="fas fa-times mr-2"></i>
        Fechar
      </b-btn>
      <b-btn
        variant="success"
        @click="save()"
        :disabled="!fields.files.length || successfully || loading"
      >
        <i class="fas fa-arrow-circle-up mr-2"></i>
        Enviar
      </b-btn>
    </template>
  </base-modal>
</template>

<script>
import makeToast from '@/mixins/makeToast';

import BaseModal from 'Components/Modal/BaseModal.vue';
import ErrorMessage from 'Components/ErrorMessage.vue';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'UploadProductsSpreadsheetModal',
  mixins: [makeToast],
  data() {
    return {
      loading: false,
      open: false,
      successfully: false,
      fields: {
        /** @type {File[]} */
        files: [],
      },
      errorMessages: {
        files: '',
      },
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
  },
  methods: {
    ...mapActions('shopkeeper', ['saveSpreadsheetProductsToImport']),
    ...mapActions('files', ['uploadSpreadsheetsToExport']),
    toggleOpen() {
      this.open = !this.open;

      this.loading = false;
      this.successfully = false;
      this.fields.files = [];
      this.errorMessages.files = '';
    },
    formatNames(files) {
      if (!files.length) return '';

      return files.length <= 2
        ? files.map((x) => x.name).join(', ')
        : `${files.length} arquivos selecionados`;
    },
    async save() {
      if (!this.fields.files.length) {
        this.errorMessages.files = 'Nenhum arquivo selecionado';
        return;
      }

      this.loading = true;

      await this.uploadAllFiles();

      this.saveSpreadsheetProductsToImport({
        userId: this.dadosUsuario.id,
        files: this.prepareFilesForRequest(),
      })
        .then((response) => {
          const { id } = response.data.data || {};
          this.makeToast('Sucesso', 'Arquivos enviados com sucesso!');

          this.successfully = !!id;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async uploadAllFiles() {
      const formData = new FormData();

      this.fields.files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });

      const response = await this.uploadSpreadsheetsToExport(formData);

      const { data } = response.data || {};

      data.forEach((savedFile, index) => {
        if (!savedFile.success || !savedFile.fileName) {
          this.$set(this.fields.files[index], 'invalid', true);
          return;
        }

        this.$set(this.fields.files[index], 'path', savedFile.filePath);
        this.$set(this.fields.files[index], 'savedName', savedFile.fileName);
      });
    },
    deleteFile(index) {
      this.$delete(this.fields.files, index);
    },
    prepareFilesForRequest() {
      const tempFiles = [];

      this.fields.files.forEach((file) => {
        if (file.invalid) return;

        tempFiles.push({
          name: file.savedName,
          path: file.path,
          originalName: file.name,
          mimeType: file.type,
          lastModifiedDate: new Date(file.lastModified).toLocaleString('pt-BR'),
          size: file.size,
        });
      });

      return tempFiles;
    },
  },
  components: {
    BaseModal,
    ErrorMessage,
  },
};
</script>